import { srcOrigin } from '@/globals/env'
import { SUPPORTED_POSHMARK_URL_REGEX_ARR } from '@/globals/variables'
import { isRef, unref } from 'vue'
import { WebsiteLogger } from './logWebsite'
import UserActivityException from '@/exceptions/UserActivityException'
import NotReportableException from '@/exceptions/NotReportableException'
import RequestException from '@/exceptions/RequestException'
import type TaskResultItem from '@/types/TaskResultItem'

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * A replacer function for JSON.stringify that limits the number of properties
 * for objects and elements for arrays.
 * * @param maxKeys The maximum number of properties/elements to keep.
 * @returns A JSON.stringify replacer function.
 */
function createTruncatingReplacer(maxKeys: number): (key: string, value: any) => any {
  return (key, value) => {
    // 1. Handle Objects
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const keys = Object.keys(value)

      // If the object has more keys than the limit, create a truncated copy
      if (keys.length > maxKeys) {
        const truncated: any = {}

        // Copy only the first 'maxKeys'
        for (let i = 0; i < maxKeys; i++) {
          const k = keys[i]
          truncated[k] = value[k]
        }

        // Add an indicator that the object was truncated
        truncated['...truncated'] = `[${keys.length - maxKeys} more properties]`

        return truncated
      }
    }

    // 2. Handle Arrays
    if (Array.isArray(value)) {
      // If the array has more elements than the limit, create a truncated copy
      if (value.length > maxKeys) {
        const truncated = value.slice(0, maxKeys)

        // Add an indicator as the last element
        truncated.push(`[...truncated: ${value.length - maxKeys} more elements]`)

        return truncated
      }
    }

    // Return the original value for all other types (primitives, Dates, Errors, etc.)
    return value
  }
}

// --- Combined Logging Function using the Truncating Replacer ---

const MAX_LOG_KEYS = 5
const truncatingReplacer = createTruncatingReplacer(MAX_LOG_KEYS)

function convertArgsToTruncatedText(args: any[]): string {
  const lines: string[] = []

  for (const arg of args) {
    if (typeof arg === 'string') {
      lines.push(arg)
    } else if (arg instanceof Error) {
      if (
        arg instanceof NotReportableException ||
        (arg instanceof UserActivityException && !arg.isReportable)
      )
        continue
      // Use stack trace for Errors
      lines.push(arg.stack || arg.toString())
      if (arg instanceof RequestException) {
        const { request, response, requestPayload } = arg
        // Log Request Metadata
        lines.push('--- Request Details ---')
        lines.push(`URL: ${request.url}`)
        lines.push(`Method: ${request.method}`)

        // Log Request Headers
        lines.push('Headers:')
        request.headers.forEach((value, name) => {
          lines.push(`  ${name}: ${value}`)
        })

        if (requestPayload) {
          lines.push('\nRequest Payload (Body):')
          lines.push(JSON.stringify(requestPayload, null, 2))
        }

        lines.push('--- Response Details ---')
        lines.push(`Status: ${response.status} ${response.statusText}`)
        lines.push(`Content-Type: ${response.headers.get('content-type') || 'N/A'}`)

        // Log Response Headers
        lines.push('Headers:')
        response.headers.forEach((value, name) => {
          lines.push(`  ${name}: ${value}`)
        })
      }
    } else {
      // Use JSON.stringify with the replacer for everything else
      try {
        // The replacer handles truncation before stringification
        lines.push(JSON.stringify(arg, truncatingReplacer, 2))
      } catch (e) {
        // Fallback for objects with circular references
        lines.push(`[Object failed to stringify: ${String(e)}]`)
      }
    }
  }

  return lines.join('\n')
}

export const log = ((...args: any[]) => {
  const isDebug = import.meta.env.VITE_APP_DEBUG === 'true'

  const unwrappedArgs = args.map((arg) => (isRef(arg) ? unref(arg) : arg))

  if (isDebug) {
    console.trace.apply(window.console, args)
  }

  if (unwrappedArgs.length > 0) {
    const logMessage = typeof unwrappedArgs[0] === 'string' ? unwrappedArgs[0] : 'Data Content'
    const logContext = convertArgsToTruncatedText(unwrappedArgs)
    if (logContext?.trim() === '') return
    WebsiteLogger.info(logMessage, logContext)
  }
}) as (...args: any[]) => void

export const logError = ((...args: any[]) => {
  const isDebug = import.meta.env.VITE_APP_DEBUG === 'true'

  if (isDebug) {
    console.error.apply(window.console, args)
    console.trace('Web Error Origin:')
  }

  const unwrappedArgs = args.map((arg) => (isRef(arg) ? unref(arg) : arg))

  if (unwrappedArgs.length > 0) {
    const logMessage = typeof unwrappedArgs[0] === 'string' ? unwrappedArgs[0] : 'Data Content'
    const logContext = convertArgsToTruncatedText(unwrappedArgs)
    if (logContext?.trim() === '') return
    WebsiteLogger.error(logMessage, logContext)
  }
}) as (...args: any[]) => void

export const calcRandomTimer = (delay: { min: number; max: number }) => {
  return Math.floor(Math.random() * (delay.max - delay.min + 1) + delay.min)
}

export const formatDate = (date: Date) => {
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }).format(date)

  return formattedDate
}

export const isValidDate = (dateString: string) => {
  return !isNaN(Date.parse(dateString))
}

export const scrollToTop = () => {
  document
    .querySelector('#poshpop-extension-app-container')
    ?.scrollTo({ top: 0, behavior: 'smooth' })
}

export const isValidUrl = (str: string) => {
  try {
    const url = new URL(str)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (err) {
    return false
  }
}

export const isValidClosetUrl = (str: string) => {
  try {
    const url = new URL(str)

    if (
      !url.hostname ||
      !(url.hostname.includes('poshmark.com') || url.hostname.includes('poshmark.ca'))
    )
      return false

    if (
      !url.pathname ||
      (!url.pathname.includes('closet') &&
        !url.pathname.includes('brand') &&
        !url.pathname.includes('/category/'))
    )
      return false

    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (err) {
    return false
  }
}

export const sendMessage = (msg: any) => {
  window.parent.postMessage(msg, srcOrigin)
}

export const handleServerError = (error: any) => {
  if (typeof error === 'object') {
    Object.values(error).forEach((err) => {
      if (typeof err === 'string') {
        throw new Error(err)
      } else if (Array.isArray(err)) {
        throw new Error(...err)
      }
    })
  } else if (typeof error === 'string') {
    throw new Error(error)
  }
}

export const isSupportedSite = (url: string) => {
  for (const regex of SUPPORTED_POSHMARK_URL_REGEX_ARR) {
    if (url.match(regex)) return true
  }
  return true
}

export function formatNumberWithCommas(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function formatDurationFromSecs(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  const parts: string[] = []

  if (hours > 0) {
    parts.push(`${hours}h`)
  }
  if (minutes > 0 || hours > 0) {
    parts.push(`${minutes}m`)
  }
  parts.push(`${remainingSeconds}s`)

  return parts.join(' ')
}

export async function retry(fn: any, retries = 3, delay = 1000) {
  try {
    return await fn()
  } catch (err) {
    if (retries === 0) throw err
    await new Promise((res) => setTimeout(res, delay))
    return retry(fn, retries - 1, delay * 2) // exponential backoff
  }
}

export const isNetworkError = (error: unknown): boolean => {
  return error instanceof TypeError && error.message === 'Failed to fetch'
}

export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text)
    console.log('Copied to clipboard!')
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

export const generateLinkForListing = (item: TaskResultItem) => {
  const encodedTitle = slugify(item.title)
  const url = item.is_user_listing
    ? `${item.poshmark_origin}/closet/${encodedTitle}`
    : `${item.poshmark_origin}/listing/${encodedTitle}-${item.id}`
  return url
}

export const slugify = (str: string) => {
  return str
    .toString()
    .normalize('NFKD') // handle accents
    .replace(/[\u0300-\u036f]/g, '') // remove accent marks
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-') // replace spaces & symbols with hyphen
    .replace(/^-+|-+$/g, '') // remove leading/trailing hyphens
}
